<template>
  <div class="chatbot-chat-container">
    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p>Loading chat session...</p>
    </div>
    
    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <div class="alert alert-danger">
        <h4>Error Loading Chat Session</h4>
        <p>{{ error }}</p>
        <button @click="goBackToSessions" class="btn btn-primary">Back to Sessions</button>
      </div>
    </div>
    
    <!-- Main Content -->
    <div v-else class="chat-layout">
      <!-- Chat Session Sidebar -->
      <ChatSessionSidebar 
        :selected-candidate="selectedCandidate"
        :chat-session="chatSession"
        :session-files="sessionFiles"
        :selected-file="selectedFile"
        :messages="messages"
        @file-added="handleFileAdded"
        @files-added="handleFilesAdded"
        @file-removed="handleFileRemoved"
        @file-selected="handleFileSelection"
      />
      
      <div class="chat-main-container">
        <!-- WebSocket Status Indicator -->
        <div class="websocket-status" :class="websocketStatusClass">
          <small>{{ websocketStatusText }}</small>
        </div>
        
        
        <!-- Separator Line -->
        <div class="chat-separator"></div>
        
        <div class="chat-component-wrapper">
      <ChatbotChat 
            :messages="messages"
            :selected-file="selectedFile"
            :session-files="sessionFiles"
            :is-session-mode="true"
            :is-processing-message="isProcessingMessage"
            :candidate-id="candidateId"
            :session-id="sessionId"
            @send-message="sendMessage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatSessionSidebar from './components/ChatSessionSidebar.vue'
import ChatbotChat from './components/ChatbotChat.vue'
import ChatbotService from "@/services/chatbot.service";

export default {
  name: 'ChatSessionPage',
  components: { 
    ChatSessionSidebar,
    ChatbotChat
  },
  props: {
    candidateId: {
      type: String,
      required: true
    },
    sessionId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      messages: [],
      selectedFile: null,
      selectedCandidate: {},
      chatSession: {},
      sessionFiles: [],
      loading: true,
      error: null,
      websocketStatus: 'disconnected',
      websocketStatusText: 'Disconnected',
      websocketStatusClass: 'text-muted',
      isProcessingMessage: false
    }
  },
  async mounted() {
    await this.initializeChatSession();
  },
  methods: {
    async initializeChatSession() {
      try {
        const candidate = await ChatbotService.getFinancingCandidate(this.candidateId);
        this.selectedCandidate = candidate;
        
        const session = await ChatbotService.getChatSessionHistory(this.candidateId, this.sessionId);
        console.log('session', session);
        this.chatSession = session;
        
        this.sessionFiles = session.files || [];
        
        this.messages = this.parseMessage(session.messages) || [];
        
        // Set first file as selected if available
        if (this.sessionFiles.length > 0) {
          this.selectedFile = this.sessionFiles[0];
        }
        
        this.loading = false;
      } catch (err) {
        this.error = 'Failed to load chat session.';
        console.error('Error loading chat session:', err);
        this.loading = false;
      }
    },

    parseMessage(messages) {
      if (!messages || !Array.isArray(messages)) return [];
      
      return messages.map(message => {
        return {
          role: message.type || message.role,
          content: message.content ? message.content: ''
        }
      }).filter(message => message.content); // Remove empty messages
    },
    
    async handleFileAdded(file) {
      try {
        this.sessionFiles.push(file);
        
        // Update the selected file if none is selected
        if (!this.selectedFile) {
          this.selectedFile = file;
        }
        
        await ChatbotService.updateChatSession(this.candidateId, this.sessionId, {
          files: this.sessionFiles
        });
        
        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
      } catch (err) {
        console.error('Error adding file to session:', err);
      }
    },
    
    async handleFilesAdded(files) {
      try {
        // Add all files to the session
        this.sessionFiles.push(...files);
        
        // Update the selected file if none is selected
        if (!this.selectedFile && files.length > 0) {
          this.selectedFile = files[0];
        }
        
        // Call chatbot service to update the session with all files
        await ChatbotService.updateChatSession(this.candidateId, this.sessionId, {
          files: this.sessionFiles
        });
        
        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
      } catch (err) {
        console.error('Error adding files to session:', err);
        this.$bvToast.toast('Error adding files: ' + (err.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      }
    },
    
    async handleFileRemoved(fileId) {
      try {
        this.sessionFiles = this.sessionFiles.filter(f => f.id !== fileId);
        
        // Clear selected file if it was removed
        if (this.selectedFile && this.selectedFile.id === fileId) {
          this.selectedFile = this.sessionFiles.length > 0 ? this.sessionFiles[0] : null;
        }
        
        await ChatbotService.updateChatSession(this.candidateId, this.sessionId, {
          files: this.sessionFiles
        });
        
        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
      } catch (err) {
        console.error('Error removing file to session:', err);
      }
    },
    
    handleFileSelection(file) {
      this.selectedFile = file;
    },
    
    async sendMessage(input) {
      if (!input.trim()) return;
      
      // Add user message
      this.messages.push({ 
        role: 'user', 
        content: input.trim() 
      });
      
      // Set processing state
      this.isProcessingMessage = true;
      
      // Add processing message with animation
      this.messages.push({ 
        role: 'assistant', 
        content: 'Thinking...',
        status: 'processing'
      });
      
      // Update session with new messages
      await ChatbotService.updateChatSession(this.candidateId, this.sessionId, {
        messages: this.messages
      });
      
      this.$nextTick(() => {
        this.scrollChatToBottom();
      });
      
      // Send the message to the chat session
      try {
        console.log('Sending message to session:', input);
        const response = await ChatbotService.sendChatMessageToSession(this.candidateId, this.sessionId, {
          content: input.trim(),
          metadata: {}
        });
        console.log('Message sent successfully:', response);
        
        // Use unified post-processing service for query tracking
        await this.trackQueryWithPostProcessing();
      } catch (err) {
        console.error('Error sending message to session:', err);
        
        // Remove processing message and add error message
        this.messages.pop(); // Remove "Thinking..." message
        
        this.messages.push({ 
          role: 'assistant', 
          content: 'Sorry, there was an error sending your message. Please try again.' 
        });
        
        // Update session with error message
        await ChatbotService.updateChatSession(this.candidateId, this.sessionId, {
          messages: this.messages
        });
        
        this.isProcessingMessage = false;
        
        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
      }
    },
    
    // Track query using unified post-processing service
    async trackQueryWithPostProcessing() {
      const taskId = `${this.candidateId}/${this.sessionId}/query-status`;
      
      this.$taskTracker.trackTask(taskId, {
        onProgress: (data) => {
          console.log('Query progress:', data);
          // Only update status messages, not content (content comes from WebSocket streaming)
          if (data.status && data.status !== 'completed') {
            const lastMessage = this.messages[this.messages.length - 1];
            // Only update if still on the initial "Thinking..." message
            if (lastMessage && lastMessage.content === 'Thinking...') {
              // Update with a status message, but don't replace with result content
              if (data.message && !data.message.includes('result') && data.message !== 'Thinking...') {
                lastMessage.content = data.message;
              }
            }
          }
        },
        onSuccess: async (data) => {
          console.log('Query completed:', data);
          
          const lastMessage = this.messages[this.messages.length - 1];
          
          if (lastMessage && lastMessage.role === 'assistant') {
            if (lastMessage.status === 'streaming' || lastMessage.status === 'processing') {
              delete lastMessage.status;
            }
            
            if (lastMessage.content === 'Thinking...') {
              if (data.result) {
                console.log('Using polling result as fallback (no WebSocket streaming occurred)');
                lastMessage.content = data.result.trim();
              }
            } else {
              console.log('Streaming completed via WebSocket, not using polling result to avoid duplication');
            }
          }
          
          await ChatbotService.updateChatSession(this.candidateId, this.sessionId, {
            messages: this.messages
          });
          
          this.isProcessingMessage = false;
          
          this.$nextTick(() => {
            this.scrollChatToBottom();
          });
        },
        onError: async (data) => {
          console.log('Query failed:', data);
          
          // Remove processing message
          if (this.messages.length > 0 && this.messages[this.messages.length - 1].content === 'Thinking...') {
            this.messages.pop();
          }
          
          // Add error message
          this.messages.push({ 
            role: 'assistant', 
            content: 'Sorry, there was an error processing your request. Please try again.' 
          });
          
          // Update session with error message
          await ChatbotService.updateChatSession(this.candidateId, this.sessionId, {
            messages: this.messages
          });
          
          this.isProcessingMessage = false;
          
          this.$nextTick(() => {
            this.scrollChatToBottom();
          });
        },
        onTimeout: async () => {
          console.log('Query timeout');
          this.handleQueryError('Query timed out. Please try again.');
        }
      });
    },

    handleQueryError(errorMessage) {
      if (this.messages.length > 0 && this.messages[this.messages.length - 1].content === 'Thinking...') {
        this.messages.pop();
      }
      
      this.messages.push({ 
        role: 'assistant', 
        content: errorMessage
      });
      
      this.isProcessingMessage = false;
      
      this.$nextTick(() => {
        this.scrollChatToBottom();
      });
    },

    scrollChatToBottom() {
      const chatBox = document.getElementById("chat");
      if (chatBox) chatBox.scrollTop = chatBox.scrollHeight;
    },


    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString();
      } catch (e) {
        return '';
      }
    },
    
    goBackToSessions() {
      this.$router.push({ 
        name: 'chatbot-candidate-sessions',
        params: { candidateId: this.candidateId }
      });
    },
    
  }
}
</script>

<style lang="scss" scoped>
@import '../chatbot/index.scss';

.chatbot-chat-container {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  margin-top: 10px;
  min-height: 600px;
}

.chat-layout {
  height: 100%;
  display: flex;
  overflow: hidden;
  position: relative;
  min-height: 600px;
}

.chat-main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.websocket-status {
  position: absolute;
  top: 5px;
  right: 15px;
  z-index: 100;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  flex-shrink: 0;
  
  &.text-success {
    color: #28a745;
  }
  
  &.text-warning {
    color: #ffc107;
  }
  
  &.text-muted {
    color: #6c757d;
  }
  
  &.text-danger {
    color: #dc3545;
  }
}

.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  max-height: 100vh;
  background-color: #f8f9fa;
  color: #343a40;
  text-align: center;
  padding: 20px;

  .spinner-border {
    width: 3rem;
    height: 3rem;
    margin-bottom: 10px;
  }

  p {
    font-size: 1rem;
    margin-bottom: 0;
  }

  .alert {
    max-width: 400px;
    margin-top: 15px;
  }
}

.chat-session-header {
  padding: 10px 20px;
  padding-bottom: 5px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;

  h3 {
    margin-bottom: 3px;
    color: #343a40;
    font-size: 1.1rem;
  }

  .session-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 5px;
    font-size: 0.85rem;
    color: #6c757d;
  }

  .candidate-name {
    font-weight: 500;
    color: #495057;
  }

  .session-date {
    color: #6c757d;
  }

  .context-files {
    margin-bottom: 5px;
    
    .context-label {
      font-weight: 500;
      color: #495057;
      margin-right: 0.5rem;
      display: block;
      margin-bottom: 0.25rem;
    }
    
    .context-files-list {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
    
    .context-file {
      display: inline-block;
      background: rgba($color-primary, 0.1);
      color: $color-primary;
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 500;
      border: 1px solid rgba($color-primary, 0.2);
      transition: all 0.2s ease;
      
      &:hover {
        background: rgba($color-primary, 0.15);
        border-color: rgba($color-primary, 0.3);
        transform: translateY(-1px);
      }
    }
  }

  .no-context {
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 0;
    font-style: italic;
  }
}

.chat-separator {
  height: 1px;
  background: linear-gradient(to right, transparent, #e9ecef, transparent);
  margin: 0 20px;
  flex-shrink: 0;
}

.chat-component-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}
</style> 